import {
  assignDurations,
  getNumberOfRows,
  isNumber,
  time2track,
} from '../api/tapi';
import app from 'app';
import _ from 'lodash-extended';
import fg from '#/libs/FrameGrid';
import PictureInPictureAdapter from 'c/PictureInPictureAdapter';
import { AudioTrackWrapper, VisualTrackWrapper } from '../api/model';
import {
  getVisualSplitContext,
  setDefaultDuration,
  setReplacementDuration,
} from '../api/asset';
import Vue from 'vue';

export default {
  INIT_TIMEMACHINE(
    { state, commit, dispatch },
    { rowDuration, timelineData, lightboxAudio, audioTracks }
  ) {
    commit('setTimeLayerRowDuration', rowDuration);
    dispatch('INIT_TRACK_LAYER', { timelineData, lightboxAudio, audioTracks });
  },

  INIT_TRACK_LAYER({ dispatch }, { timelineData, lightboxAudio, audioTracks }) {
    dispatch('INIT_TRACK_LAYER_VISUAL', { timelineData });
    dispatch('INIT_TRACK_LAYER_AUDIO', { lightboxAudio, audioTracks });
  },

  async SET_TIME_LAYER_ROWS({ state, commit, dispatch, getters }) {
    // @note here awaiting gives opportunity to fire `ready` event
    await dispatch('SET_STATE', { name: 'INITIALIZING', value: true });
    commit('calculateCssVariables', {
      hasTranscriptTrack: getters.shouldShow_TrackLayerTranscript,
      hasAudioTrack: getters.shouldShow_TrackLayerAudio,
    });
    commit('setTimeLayerRows', {
      numberOfRows: getNumberOfRows({
        rowDuration: state.timeLayer.rowDuration,
        maxLayerDuration: getters.maxLayerDuration,
      }),
    });
    dispatch('SET_STATE', { name: 'INITIALIZING', value: false });
  },

  // undo/redo use case
  UPDATE_TIMEMACHINE_DATA(
    { state, dispatch },
    { timelineData, lightboxAudio, audioTracks }
  ) {
    dispatch('UNSELECT_TRACKS');
    dispatch('INIT_TRACK_LAYER', { timelineData, lightboxAudio, audioTracks });
    return dispatch('SET_TIME_LAYER_ROWS');
  },

  RECALCULATE_ROWS({ state, commit, getters }) {
    const neededNumberOfRows = getNumberOfRows({
      rowDuration: state.timeLayer.rowDuration,
      maxLayerDuration: getters.maxLayerDuration,
    });
    const delta = neededNumberOfRows - state.timeLayer.rows.length;

    if (delta > 0) {
      for (let i = 0, I = delta; i < I; i++) {
        commit('appendTimeLayerRow');
      }
    } else if (delta < 0) {
      for (let i = 0, I = -delta; i < I; i++) {
        commit('popTimeLayerRow');
      }
    }
  },

  INIT_TRACK_LAYER_VISUAL({ commit, dispatch }, { timelineData }) {
    commit('initTrackLayerVisual', { timelineData });
    dispatch('SET_VISUAL_LAYER_DURATION');
  },

  INIT_TRACK_LAYER_AUDIO({ commit, dispatch }, { lightboxAudio, audioTracks }) {
    commit('initTrackLayerAudio', { lightboxAudio, audioTracks });
    dispatch('SET_AUDIO_LAYER_DURATION');
  },

  SET_VISUAL_LAYER_DURATION({ state, commit }) {
    let visualDuration = 0;
    if (state.trackLayer.visual.length) {
      const lastTrack =
        state.trackLayer.visual[state.trackLayer.visual.length - 1];
      visualDuration = lastTrack.data.displayEndTime;
    }
    commit('setVisualLayerDuration', visualDuration);
  },

  SET_AUDIO_LAYER_DURATION({ state, commit }, customDuration) {
    let audioDuration = 0;

    if (isNumber(customDuration)) {
      audioDuration = customDuration;
    } else if (state.trackLayer.audio.length) {
      state.trackLayer.audio.sort(
        (a, b) => a.data.displayStartTime - b.data.displayStartTime
      );
      const lastTrack =
        state.trackLayer.audio[state.trackLayer.audio.length - 1];
      audioDuration = lastTrack.data.displayEndTime;
    }

    commit('setAudioLayerDuration', audioDuration);
  },

  SET_AXIS({ commit }, axis) {
    if (0 !== axis.width) {
      commit('setAxis', axis);
    } else {
      console.error(`SET_AXIS: bad width: '${axis.width}'`);
    }
  },

  SET_SCROLL_TOP({ commit }, scrollTop) {
    commit('setScrollTop', scrollTop);
  },

  SET_SECONDHAND({ state, commit, getters }, { time, makeItVisible }) {
    if (!isNumber(time)) {
      app.doctorController.report(`SET_SECONDHAND: bad time ${time}`);
      return;
    } else if (state.MODE.SCENE_EDIT) {
      const { data } = state.sceneEscort.sceneTrack;
      time = _.constrain(data.displayStartTime, time, data.displayEndTime);
    } else {
      time = _.constrain(0, time, getters.maxLayerDuration);
    }

    commit('setSecondhand', { time: fg.trunc(time), makeItVisible });
  },

  /**
   * set mode to value OR toggle it
   */
  SET_MODE({ state, commit }, { name, value, toggle }) {
    if (
      Object.getOwnPropertyDescriptor(state.MODE, name) &&
      (typeof value === 'boolean' || typeof toggle === 'boolean')
    ) {
      if (typeof toggle === 'boolean') {
        value = !state.MODE[name];
      }
      commit('setMODE', { name, value });
    } else {
      app.doctorController.report(`bad SET_MODE("${name}")`);
    }
  },

  SET_STATE({ state, commit }, { name, value }) {
    if (
      Object.getOwnPropertyDescriptor(state.STATE, name) &&
      typeof value === 'boolean'
    ) {
      commit('setSTATE', { name, value });
    } else {
      app.doctorController.report(`bad SET_STATE("${name}", "${value}")`);
    }
  },

  SET_USE({ state, commit }, { name, value, toggle }) {
    if (Object.getOwnPropertyDescriptor(state.USE, name)) {
      if (typeof toggle === 'boolean') {
        value = !state.USE[name];
      }
      app.remoteStash.set(name, !!value);
      commit('setUSE', { name, value });
    } else {
      app.doctorController.report(`bad SET_USE("${name}", "${value}")`);
    }
  },

  SET_DND_ESCORT({ state, commit }, { name, value }) {
    if (Object.getOwnPropertyDescriptor(state.dndEscort, name)) {
      commit('setDndEscort', { name, value });
    } else {
      app.doctorController.report(`bad SET_DND_ESCORT("${name}", "${value}")`);
    }
  },

  SET_SCENE_ESCORT({ state, commit }, { name, value }) {
    if (Object.getOwnPropertyDescriptor(state.sceneEscort, name)) {
      commit('setSceneEscort', { name, value });
    } else {
      app.doctorController.report(
        `bad SET_SCENE_ESCORT("${name}", "${value}")`
      );
    }
  },

  async CHANGE_ROW_DURATION({ commit, dispatch }, newDuration) {
    commit('setTimeLayerRowDuration', newDuration);
    await dispatch('SET_TIME_LAYER_ROWS');
  },

  /**
   * select visual by time or by track or by guid
   */
  SELECT_VISUAL({ state, commit, dispatch }, { time, guid, track }) {
    if (isNumber(time)) {
      track = time2track(state.trackLayer.visual, time);
    } else if (guid) {
      track = _.find(state.trackLayer.visual, { guid });
    }

    commit('setVisualSelected', track);
    commit('setAudioSelected', null);
  },

  SELECT_AUDIO({ state, commit }, { time, track }) {
    if (isNumber(time)) {
      track = time2track(state.trackLayer.audio, time);
    }
    commit('setVisualSelected', null);
    commit('setAudioSelected', track);
  },

  UNSELECT_TRACKS({ commit }) {
    commit('setVisualSelected', null);
    commit('setAudioSelected', null);
  },

  /**
   * select overlay by time or by overlay
   */
  SELECT_OVERLAY({ state, commit, dispatch }, { time, overlay, log }) {
    if (isNumber(time)) {
      overlay = state.sceneEscort.actingPipa.find({ absoluteTime: time });
    }

    commit('setVisualOverlaySelected', overlay);

    if (log) {
      console.log('overlay', overlay);
    }
  },

  APPLY_EDITED_ASSET({ state, dispatch }, { editedAsset, durationChange }) {
    const track = _.find(state.trackLayer.visual, { guid: editedAsset.guid });

    if (track) {
      for (const field in editedAsset) {
        if (Object.getOwnPropertyDescriptor(editedAsset, field)) {
          Vue.set(track.data, field, editedAsset[field]);
        }
      }
      if (durationChange) {
        dispatch('SHIFT_VISUALS', { beforeTrack: track });
      }
    }

    return track;
  },

  SHIFT_VISUALS({ state, commit, dispatch }, o) {
    o = Object.assign(
      {
        beforeTrack: null,
        afterTrack: null,
      },
      o
    );

    let fromIndex = -1;
    let time = 0;

    if (o.beforeTrack) {
      fromIndex = state.trackLayer.visual.indexOf(o.beforeTrack);
      if (fromIndex > 0) {
        time = state.trackLayer.visual[fromIndex - 1].data.displayEndTime;
      }
    } else if (o.afterTrack) {
      fromIndex = state.trackLayer.visual.indexOf(o.afterTrack);
      time = state.trackLayer.visual[fromIndex].data.displayEndTime;
      ++fromIndex;
    }

    for (let i = fromIndex, I = state.trackLayer.visual.length; i < I; i++) {
      const data = state.trackLayer.visual[i].data;

      commit('setTrackDuration', {
        data,
        start: time,
        duration: data.displayDuration,
        end: time + data.displayDuration,
      });

      time += data.displayDuration;
    }

    dispatch('SET_VISUAL_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
  },

  REMOVE_VISUAL({ state, commit, dispatch }, track) {
    // find index
    const trackIndex = state.trackLayer.visual.indexOf(track);
    if (trackIndex < 0) {
      app.doctorController.report('REMOVE_VISUAL: track not found');
      return Promise.reject();
    }

    // remove
    commit('setVisualSelected', null);
    commit('removeVisualTrack', { atIndex: trackIndex });

    // rearrange
    let time = 0;
    for (let n = 0, N = state.trackLayer.visual.length; n < N; n++) {
      const data = state.trackLayer.visual[n].data;

      commit('setTrackDuration', {
        data,
        start: time,
        duration: data.displayDuration,
        end: time + data.displayDuration,
      });

      time += data.displayDuration;
    }

    dispatch('SET_VISUAL_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });

    return { trackIndex };
  },

  UX_AFTER_REMOVE_VISUAL(
    { state, dispatch },
    { removedTrackIndex, toTheLeft = true }
  ) {
    let seekToTime = 0;

    // select next track
    const nextIndex = _.constrain(
      0,
      toTheLeft ? removedTrackIndex - 1 : removedTrackIndex,
      state.trackLayer.visual.length - 1
    );
    const nextTrack = state.trackLayer.visual[nextIndex];
    if (nextTrack) {
      dispatch('SELECT_VISUAL', { track: nextTrack });
      // set secondhand to half-time of prev asset
      seekToTime =
        fg.trunc(
          nextTrack.data.displayStartTime + nextTrack.data.displayEndTime
        ) / 2;
    }

    dispatch('SET_SECONDHAND', { time: seekToTime });
    return { seekToTime };
  },

  DUPLICATE_VISUAL({ state, commit, dispatch }, track) {
    const trackIndex = state.trackLayer.visual.indexOf(track);

    const copy = PictureInPictureAdapter.duplicateScene({
      scene: track.data,
      assignNewGuid: true,
    });
    const newTrack = new VisualTrackWrapper({ assetCopy: copy });

    commit('insertVisualTrack', { atIndex: trackIndex + 1, track: newTrack });
    dispatch('SHIFT_VISUALS', { afterTrack: track });
    dispatch('SELECT_VISUAL', { track: newTrack });
    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });

    return { newTrack };
  },

  SPLIT_VISUAL({ state, commit, dispatch }, { track, time }) {
    const trackIndex = state.trackLayer.visual.indexOf(track);
    const context = getVisualSplitContext(track.data, time);

    const copy = PictureInPictureAdapter.duplicateScene({
      scene: track.data,
      assignNewGuid: true,
    });
    const newTrack = new VisualTrackWrapper({ assetCopy: copy });

    // trim original
    if (_.isDefined(track.data.endTime)) {
      track.data.endTime = context.original.endTime;
    }
    track.data.displayEndTime = context.original.displayEndTime;
    track.data.displayDuration = context.original.displayDuration;

    // trim copy
    if (_.isDefined(newTrack.data.startTime)) {
      newTrack.data.startTime = context.clone.startTime;
    }
    newTrack.data.displayStartTime = context.clone.displayStartTime;
    newTrack.data.displayDuration = context.clone.displayDuration;

    PictureInPictureAdapter.sceneSplit({
      originalAsset: track.data,
      cloneAsset: newTrack.data,
    });

    commit('insertVisualTrack', { atIndex: trackIndex + 1, track: newTrack });
    dispatch('SHIFT_VISUALS', { beforeTrack: track });
    dispatch('SELECT_VISUAL', { track: newTrack });

    return {
      originalAsset: track.data,
      cloneAsset: newTrack.data,
      assetSplitTimes: context,
    };
  },

  SPLIT_AUDIO({ commit, dispatch }, { track, time }) {
    const relativeSplitTime = fg.ceil(
      track.data.relativeStartTime + (time - track.data.displayStartTime)
    );
    const clone = track.clone();

    track.data.displayEndTime = time;
    track.data.displayDuration = fg.ceil(time - track.data.displayStartTime);
    track.data.relativeEndTime = relativeSplitTime;

    clone.data.displayStartTime = time;
    clone.data.displayDuration = fg.ceil(clone.data.displayEndTime - time);
    clone.data.relativeStartTime = relativeSplitTime;

    commit('insertAudioTrack', clone);
    dispatch('SELECT_AUDIO', { track: clone });
  },

  INSERT_VISUAL_ASSET(
    { state, commit, dispatch, rootGetters },
    { time, asset, appendToEnd }
  ) {
    const newTrack = new VisualTrackWrapper({ assetCopy: asset });
    // shorthand for appending to the end
    if (appendToEnd && !isNumber(time)) {
      time = state.trackLayer.visualDuration;
    }

    setDefaultDuration({
      asset: newTrack.data,
      programmer: rootGetters.storyProgrammer,
      isExternal: true,
      isInSceneEdit: state.MODE.SCENE_EDIT,
    });

    dispatch('INSERT_VISUAL_TRACK', { time, track: newTrack });
    dispatch('SELECT_VISUAL', { track: newTrack });

    return newTrack;
  },

  INSERT_VISUAL_TRACK({ state, commit, dispatch }, { time, track }) {
    const insertionIndex =
      _.findIndexInSorted(state.trackLayer.visual, (track) => {
        const et = track.data.displayEndTime;
        return time < et ? -1 : time <= et ? 0 : 1;
      }) ?? -1;

    commit('insertVisualTrack', { atIndex: insertionIndex + 1, track: track });
    dispatch('SHIFT_VISUALS', { beforeTrack: track });
  },

  REPLACE_VISUAL_TRACK(
    { state, commit, dispatch, rootGetters },
    { track2replace, asset }
  ) {
    const newTrack = new VisualTrackWrapper({ assetCopy: asset });

    setReplacementDuration({
      asset: newTrack.data,
      asset2replace: track2replace.data,
    });

    if (newTrack.data.displayDuration < track2replace.data.displayDuration) {
      newTrack.pipa.cutFromRight(newTrack.data.displayDuration);
    }

    commit('replaceVisualTrack', {
      atIndex: state.trackLayer.visual.indexOf(track2replace),
      newTrack,
    });

    dispatch('SHIFT_VISUALS', { beforeTrack: newTrack });
    dispatch('SELECT_VISUAL', { track: newTrack });

    return newTrack;
  },

  /**
   * Show/hide the object tracking banner on a visual track.
   * Driven by app.objectTrackingController.
   */
  VISUAL_TRACKING_STATE_CHANGED({ state, commit }, { guid, pending }) {
    const track = _.find(state.trackLayer.visual, { guid });

    if (track) {
      commit('setVisualTrackingState', { track, pending });
    }
  },

  VISUAL_RTP_STATUS_CHANGED(
    { state, commit },
    {
      realTimePreviewResultData,
      realTimeFetchProgress,
      realTimePreviewChecksum,
      status,
    }
  ) {
    const tracks = [].concat(state.trackLayer.visual);
    if (state.dndEscort.trackInHand && state.MODE.VISUAL_DND) {
      tracks.push(state.dndEscort.trackInHand);
    }

    for (let n = 0, N = tracks.length; n < N; n++) {
      const track = tracks[n];

      if (track.data._tmpRealTimePreviewChecksum === realTimePreviewChecksum) {
        commit('setVisualRtpState', {
          track,
          status,
          percent: realTimeFetchProgress,
        });

        commit('setVisualRtpByStatus', {
          track,
          status,
          realTimePreviewChecksum,
          realTimePreviewResultData,
        });
      }
    }
  },

  SCENE_EDIT_MODE_ENTER(
    { state, commit, dispatch },
    { track, byHover = false }
  ) {
    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });
    dispatch('SET_MODE', { name: 'SCENE_EDIT', toggle: true });
    dispatch('SELECT_VISUAL', { track });
    commit('setSceneEscort', { name: 'sceneTrack', value: track });
    commit('setSceneEscort', { name: 'byHover', value: byHover });

    const copy = PictureInPictureAdapter.duplicateScene({
      scene: track.data,
      assignNewGuid: false,
    });
    const actingPipa = new PictureInPictureAdapter({ scene: copy });
    commit('setSceneEscort', { name: 'actingPipa', value: actingPipa });

    return { sceneTrack: track };
  },

  SCENE_EDIT_MODE_APPLY({ state, dispatch }, { keepItSelected }) {
    const { sceneTrack, actingPipa } = state.sceneEscort;

    actingPipa.apply({ originalScene: sceneTrack.data });
    dispatch('SCENE_EDIT_MODE_LEAVE', { keepItSelected });

    return { sceneTrack };
  },

  SCENE_EDIT_MODE_CANCEL({ state, dispatch }) {
    const { sceneTrack } = state.sceneEscort;

    dispatch('SCENE_EDIT_MODE_LEAVE', { keepItSelected: true });

    return { sceneTrack };
  },

  /**
   * performs UI related changes after apply/cancel
   */
  SCENE_EDIT_MODE_LEAVE({ state, commit, dispatch }, { keepItSelected }) {
    dispatch('SELECT_VISUAL', {
      track: keepItSelected ? state.sceneEscort.sceneTrack : null,
    });
    commit('setVisualOverlaySelected', null);
    commit('setSceneEscort', { name: 'sceneTrack', value: null });
    commit('setSceneEscort', { name: 'actingPipa', value: null });
    dispatch('SET_MODE', { name: 'SCENE_EDIT', value: false });
    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });
  },

  DUPLICATE_VISUAL_OVERLAY({ state, dispatch }, { overlay }) {
    const { actingPipa } = state.sceneEscort;
    const newOverlay = actingPipa.duplicateOverlay(overlay.guid);

    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });

    return { actingPipa, newOverlay };
  },

  REMOVE_VISUAL_OVERLAY({ state, commit, dispatch }, { overlay }) {
    const { actingPipa } = state.sceneEscort;

    commit('setVisualOverlaySelected', null);
    actingPipa.delete(overlay.guid);

    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });

    return { actingPipa };
  },

  /**
   * add audio `track` at `time` or `appendToEnd`
   * @returns {{AudioTrackWrapper}}
   */
  INSERT_AUDIO({ state, commit, dispatch }, { track, time, appendToEnd }) {
    // shorthand for passing raw asset data and not AudioTrackWrapper
    if (!(track instanceof AudioTrackWrapper)) {
      track = new AudioTrackWrapper({
        selected: track,
        audioOnTrack: {
          /* placeholder */
        },
      });
    }

    // shorthand for appending to the end
    if (appendToEnd && !isNumber(time)) {
      time = state.trackLayer.audioDuration;
    }

    let newAudioDuration = track.durationLimits.max;
    let newDurationTooSmall = false;
    const trackOnTime = time2track(state.trackLayer.audio, time);
    const isInsideOtherTrack =
      trackOnTime &&
      trackOnTime.guid !== track.guid &&
      time < trackOnTime.data.displayEndTime;

    if (!isInsideOtherTrack) {
      const nextTrackIndex = _.findIndex(state.trackLayer.audio, (t) => {
        return time < t.data.displayStartTime;
      });
      if (nextTrackIndex >= 0) {
        const nextTrack = state.trackLayer.audio[nextTrackIndex];
        // if no space - trim from right...
        newAudioDuration = Math.min(
          fg.trunc(nextTrack.data.displayStartTime - time),
          newAudioDuration
        );
        if (newAudioDuration < track.durationLimits.min) {
          newDurationTooSmall = true;
        }
      }
    }

    if (isInsideOtherTrack || newDurationTooSmall) {
      // if conflicting - remove doppler and nullify returned track
      track = null;
    } else {
      commit('insertAudioTrack', track);

      assignDurations(track.data, {
        displayStartTime: fg.trunc(time),
        displayEndTime: fg.trunc(time + newAudioDuration),
        relativeStartTime: 0,
        relativeEndTime: newAudioDuration,
      });
    }

    dispatch('SET_AUDIO_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
    dispatch('SELECT_AUDIO', { track });
    dispatch('SET_SECONDHAND', {
      time: track.data.displayStartTime,
      makeItVisible: true,
    });

    return { track };
  },

  /**
   * Assuming track landing time is free
   * If track end-time not covers other tracks - insert and finish.
   * If track end-time covers other tracks - push them forward
   * - then - allow option to bring them back on the price of trimming current track
   */
  MANAGED_AUDIO_INSERT({ state, commit, dispatch }, { track, time }) {
    let iPushFrom = null;
    for (let n = 0, N = state.trackLayer.audio.length; n < N; n++) {
      const cur = state.trackLayer.audio[n];
      if (
        _.between(
          track.data.displayStartTime,
          cur.data.displayStartTime,
          track.data.displayEndTime,
          {
            includeMax: false,
          }
        )
      ) {
        iPushFrom = n;
        break;
      }
    }

    if (null !== iPushFrom) {
      // push required
      const track2pushFrom = state.trackLayer.audio[iPushFrom];
      const time2shift =
        track.data.displayEndTime - track2pushFrom.data.displayStartTime;
      const audioTracksBeforePush = [];

      for (let n = iPushFrom, N = state.trackLayer.audio.length; n < N; n++) {
        const cur = state.trackLayer.audio[n];

        audioTracksBeforePush.push({
          guid: cur.guid,
          displayStartTime: cur.data.displayStartTime,
          displayDuration: cur.data.displayDuration,
          displayEndTime: cur.data.displayEndTime,
        });

        commit('setTrackDuration', {
          data: cur.data,
          start: cur.data.displayStartTime + time2shift,
          duration: cur.data.displayDuration,
          end: cur.data.displayEndTime + time2shift,
        });
      }

      dispatch('SET_DND_ESCORT', {
        name: 'audioTracksBeforePush',
        value: audioTracksBeforePush,
      });
      commit('insertAudioTrack', track);
      dispatch('SET_MODE', { name: 'OPTIONAL_AUDIO_INSERT', value: true });
    } else {
      // can be inserted without push
      commit('insertAudioTrack', track);
      dispatch('SET_DND_ESCORT', { name: 'trackInHand', value: null });
    }

    dispatch('SELECT_AUDIO', { track });
    dispatch('SET_AUDIO_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
    dispatch('SET_SECONDHAND', {
      time: track.data.displayStartTime,
      makeItVisible: true,
    });

    return { track };
  },

  /**
   * assume current state of audio is valid - finish this mode
   */
  MANAGED_AUDIO_INSERT_EXIT({ dispatch }) {
    dispatch('SET_DND_ESCORT', { name: 'trackInHand', value: null });
    dispatch('SET_DND_ESCORT', { name: 'audioTracksBeforePush', value: null });
    dispatch('SET_MODE', { name: 'OPTIONAL_AUDIO_INSERT', value: false });

    dispatch('SET_AUDIO_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
  },

  MANAGED_AUDIO_INSERT_UNSHIFT({ state, commit }) {
    for (
      let n = 0, N = state.dndEscort.audioTracksBeforePush.length;
      n < N;
      n++
    ) {
      const remembered = state.dndEscort.audioTracksBeforePush[n];
      const actual = _.find(state.trackLayer.audio, { guid: remembered.guid });

      if (actual) {
        commit('setTrackDuration', {
          data: actual.data,
          start: remembered.displayStartTime,
          duration: remembered.displayDuration,
          end: remembered.displayEndTime,
        });
      }
    }
  },

  MANAGED_AUDIO_INSERT_TRIM({ state, dispatch }) {
    const firstPushedTrack = state.dndEscort.audioTracksBeforePush[0];
    const timeToTrim = firstPushedTrack.displayStartTime;
    const track = state.dndEscort.trackInHand;
    const newAudioDuration = timeToTrim - track.data.displayStartTime;

    track.data.displayDuration = newAudioDuration;
    track.data.displayEndTime = timeToTrim;
    track.data.relativeStartTime = 0;
    track.data.relativeEndTime = fg.trunc(newAudioDuration);

    dispatch('MANAGED_AUDIO_INSERT_UNSHIFT');
    dispatch('MANAGED_AUDIO_INSERT_EXIT');
  },

  MANAGED_AUDIO_INSERT_DELETE({ state, commit, dispatch }) {
    const index = state.trackLayer.audio.indexOf(state.dndEscort.trackInHand);
    commit('removeAudioTrack', { atIndex: index });
    commit('setAudioSelected', null);

    dispatch('MANAGED_AUDIO_INSERT_UNSHIFT');
    dispatch('MANAGED_AUDIO_INSERT_EXIT');
  },

  REMOVE_AUDIO({ state, commit, dispatch }, track) {
    const trackIndex = state.trackLayer.audio.indexOf(track);

    if (trackIndex >= 0) {
      commit('setAudioSelected', null);
      commit('removeAudioTrack', { atIndex: trackIndex });

      dispatch('SET_AUDIO_LAYER_DURATION');
      dispatch('RECALCULATE_ROWS');
    }

    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });

    return { trackIndex };
  },

  UX_AFTER_REMOVE_AUDIO(
    { state, dispatch },
    { removedTrackIndex, toTheLeft = true }
  ) {
    let seekToTime = 0;

    // select next track
    const nextIndex = _.constrain(
      0,
      toTheLeft ? removedTrackIndex - 1 : removedTrackIndex,
      state.trackLayer.audio.length - 1
    );
    const nextTrack = state.trackLayer.audio[nextIndex];
    if (nextTrack) {
      dispatch('SELECT_AUDIO', { track: nextTrack });
      // set secondhand to half-time of prev asset
      seekToTime =
        fg.trunc(
          nextTrack.data.displayStartTime + nextTrack.data.displayEndTime
        ) / 2;
    }

    dispatch('SET_SECONDHAND', { time: seekToTime });
    return { seekToTime };
  },

  SHOW_VISUAL_AUDIO_GAP({ dispatch }) {
    dispatch('SET_MODE', { name: 'VA_GAP_VISIBLE', value: true });
  },

  AUDIO_MULTISELECT_ENTER({ dispatch }, { index }) {
    dispatch('SET_DND_ESCORT', { name: 'multiSelectIndex', value: index });
    dispatch('SET_MODE', { name: 'AUDIO_MULTISELECT', value: true });
    dispatch('SET_MODE', { name: 'SECONDHAND_CUT', value: false });
  },

  AUDIO_MULTISELECT_DELETE({ state, commit, dispatch }) {
    while (state.dndEscort.multiSelectIndex < state.trackLayer.audio.length) {
      commit('removeAudioTrack', { atIndex: state.dndEscort.multiSelectIndex });
    }

    if (state.trackLayer.audio.length) {
      const lastTrack =
        state.trackLayer.audio[state.trackLayer.audio.length - 1];
      commit('setAudioSelected', lastTrack);
    } else {
      commit('setAudioSelected', null);
    }

    dispatch('SET_AUDIO_LAYER_DURATION');
    dispatch('RECALCULATE_ROWS');
    dispatch('AUDIO_MULTISELECT_EXIT');
  },

  AUDIO_MULTISELECT_EXIT({ dispatch }) {
    dispatch('SET_DND_ESCORT', { name: 'multiSelectIndex', value: null });
    dispatch('SET_MODE', { name: 'AUDIO_MULTISELECT', value: false });
  },

  TOGGLE_BEATS({ state, dispatch, rootState }) {
    dispatch('SET_USE', { name: 'showBeats', toggle: true });

    const mode = state.USE.showBeats ? 'Show' : 'Hide';
    const musicPace =
      rootState.story?.storyBackgroundMusic?.metadata?.pace || 'Unknown';
    const zoomLevel = rootState.story?.timelineZoom?.rowDuration || 'Unknown';
    app.analytics('bgMusicBeats:modeChange', {
      Mode: mode,
      'Music pace': musicPace,
      'Zoom level': zoomLevel,
    });
  },

  TOGGLE_SCENES({ state, dispatch, rootState }) {
    dispatch('SET_USE', { name: 'showTimelineScenes', toggle: true });
  },
};
